import { AlertTriangle, Check, Copy, KeyRound, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import {
  useApiKeys,
  useCreateApiKey,
  useCreateInvitation,
  useInvitations,
  useMembers,
  useOrganization,
  useRemoveMember,
  useRevokeApiKey,
  useRevokeInvitation,
  useUpdateMemberRole,
  useUpdateOrganization,
} from "@/api/hooks";
import { ApiError } from "@/api/problem";
import type { ApiKey, ApiKeyCreated, ApiKeyScope, Member, OrganizationRole } from "@/api/types";
import { useSession } from "@/auth/session";
import { PageHeader } from "@/components/app-shell";
import { Dialog } from "@/components/ui/dialog";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Field,
  Input,
  MicroLabel,
  Num,
  NumberInput,
} from "@/components/ui/primitives";
import { Select } from "@/components/ui/select";
import { EmptyState, ErrorState, FormError, LoadingRows } from "@/components/ui/states";
import type { TKey } from "@/i18n/dict";
import { useI18n } from "@/i18n/provider";
import { formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";

/**
 * Settings.
 *
 * Two rules run through this whole screen.
 *
 * 1. `dry` is not available here. It means "fuel is running out" and nothing else, so the
 *    remove-member button, the revoke-key button and the once-only key warning are all
 *    built without it (DESIGN.md §1). What makes the key warning unmissable is a slab of
 *    CREAM — on a plum page that is the loudest thing there is, which is the same reason
 *    the primary button is made of it.
 * 2. An organization can never lose its last owner. The server refuses (409), so the UI
 *    must not offer it — and, more usefully, must say how to hand ownership over instead.
 */

const ROLE_KEY: Record<OrganizationRole, TKey> = {
  owner: "role.owner",
  admin: "role.admin",
  operator: "role.operator",
  viewer: "role.viewer",
};

const ROLE_HINT: Record<OrganizationRole, TKey> = {
  owner: "role.owner.hint",
  admin: "role.admin.hint",
  operator: "role.operator.hint",
  viewer: "role.viewer.hint",
};

type Tab = "profile" | "members" | "keys";

export function SettingsPage() {
  const { t } = useI18n();
  const { can, role } = useSession();
  const [tab, setTab] = useState<Tab>("profile");

  const tabs: Array<{ id: Tab; label: TKey; visible: boolean }> = [
    { id: "profile", label: "settings.profile", visible: true },
    // Managing people is an owner/admin job. An operator has no business here and, more to
    // the point, no control on this tab would do anything for them.
    { id: "members", label: "settings.members", visible: can("members:manage") },
    // An admin may LIST keys; only an owner may mint or revoke one. Both see the tab.
    { id: "keys", label: "settings.apiKeys", visible: can("apiKeys:read") },
  ];

  const visible = tabs.filter((item) => item.visible);

  return (
    <>
      <PageHeader title={t("settings.title")} />

      <div
        role="tablist"
        aria-label={t("settings.title")}
        className="mb-5 flex gap-1 border-b border-rule"
      >
        {visible.map((item) => (
          <button
            key={item.id}
            role="tab"
            type="button"
            aria-selected={tab === item.id}
            onClick={() => setTab(item.id)}
            className={cn(
              "-mb-px border-b-2 px-3 py-2 text-sm transition-colors",
              tab === item.id
                ? // Cream, not ink: an ink underline on a plum page is invisible.
                  "border-cream font-medium text-text"
                : "border-transparent text-muted hover:text-text",
            )}
          >
            {t(item.label)}
          </button>
        ))}
      </div>

      {tab === "profile" ? <OrgProfile /> : null}
      {tab === "members" ? <MembersTab /> : null}
      {tab === "keys" ? <ApiKeysTab isOwner={role === "owner"} /> : null}
    </>
  );
}

/* ------------------------------------------------------------- profile */

/**
 * The organization, and the two numbers that actually do something.
 *
 * `low_fuel_threshold_percent` and `refill_overdue_days` are not preferences — they are
 * the trigger conditions for the emails the backend sends. So they are worded as what they
 * CAUSE, and the sentence underneath updates as the number is typed: you can read the
 * consequence of the setting before you save it.
 */
function OrgProfile() {
  const { t } = useI18n();
  const { can } = useSession();
  const orgQuery = useOrganization();
  const update = useUpdateOrganization();

  const [name, setName] = useState("");
  const [threshold, setThreshold] = useState("");
  const [overdue, setOverdue] = useState("");
  const [contact, setContact] = useState("");

  const org = orgQuery.data;

  useEffect(() => {
    if (!org) return;
    setName(org.name);
    setThreshold(String(org.settings.low_fuel_threshold_percent));
    setOverdue(String(org.settings.refill_overdue_days));
    setContact(org.settings.contact_email ?? "");
  }, [org]);

  if (orgQuery.isPending) return <LoadingRows rows={4} />;
  if (orgQuery.error)
    return <ErrorState error={orgQuery.error} onRetry={() => void orgQuery.refetch()} />;
  if (!org) return null;

  const editable = can("org:update");
  const fieldErrors = update.error instanceof ApiError ? update.error.fieldErrors : {};

  return (
    <Card>
      <CardBody>
        <form
          className="flex max-w-lg flex-col gap-6"
          onSubmit={(event) => {
            event.preventDefault();
            update.mutate({
              name: name.trim(),
              settings: {
                low_fuel_threshold_percent: Number(threshold),
                refill_overdue_days: Number(overdue),
                ...(contact.trim() ? { contact_email: contact.trim() } : {}),
              },
            });
          }}
          noValidate
        >
          <FormError
            error={
              update.error instanceof ApiError && update.error.isFormLevel ? update.error : null
            }
          />

          {update.isSuccess ? (
            <p role="status" className="flex items-center gap-1.5 text-sm text-ok">
              <Check className="size-4" aria-hidden />
              {t("settings.saved")}
            </p>
          ) : null}

          <Field label={t("settings.org.name")} error={fieldErrors["name"]}>
            {(props) => (
              <Input
                {...props}
                value={name}
                disabled={!editable}
                onChange={(event) => setName(event.target.value)}
              />
            )}
          </Field>

          <Field label={t("settings.org.slug")} hint={t("settings.org.slugImmutable")}>
            {(props) => (
              // Immutable by contract. Rendering it as an editable box the server would
              // ignore would be a small lie, and small lies are how trust goes.
              <Input
                {...props}
                dir="ltr"
                value={org.slug}
                disabled
                readOnly
                className="font-mono"
              />
            )}
          </Field>

          {/* ---- the settings that send email ---- */}
          <div className="border-t border-rule pt-6">
            <MicroLabel>{t("settings.org.section.alerts")}</MicroLabel>

            <div className="mt-4 flex flex-col gap-6">
              <Field
                label={`${t("settings.org.threshold")} (%)`}
                error={fieldErrors["settings.low_fuel_threshold_percent"]}
              >
                {(props) => (
                  <div className="flex flex-col gap-2">
                    <NumberInput
                      {...props}
                      min={1}
                      max={90}
                      value={threshold}
                      disabled={!editable}
                      onChange={(event) => setThreshold(event.target.value)}
                    />
                    <p className="text-xs text-muted">
                      {t("settings.org.thresholdSentence", { n: threshold || "—" })}
                    </p>
                  </div>
                )}
              </Field>

              <Field
                label={t("settings.org.overdue")}
                error={fieldErrors["settings.refill_overdue_days"]}
              >
                {(props) => (
                  <div className="flex flex-col gap-2">
                    <NumberInput
                      {...props}
                      min={1}
                      max={365}
                      value={overdue}
                      disabled={!editable}
                      onChange={(event) => setOverdue(event.target.value)}
                    />
                    <p className="text-xs text-muted">
                      {t("settings.org.overdueSentence", { n: overdue || "—" })}
                    </p>
                  </div>
                )}
              </Field>

              <Field
                label={t("settings.org.contact")}
                hint={t("settings.org.contactHint")}
                error={fieldErrors["settings.contact_email"]}
              >
                {(props) => (
                  <Input
                    {...props}
                    type="email"
                    dir="ltr"
                    value={contact}
                    disabled={!editable}
                    onChange={(event) => setContact(event.target.value)}
                  />
                )}
              </Field>
            </div>
          </div>

          {editable ? (
            <Button type="submit" disabled={update.isPending} className="self-start">
              {update.isPending ? t("common.saving") : t("common.save")}
            </Button>
          ) : null}
        </form>
      </CardBody>
    </Card>
  );
}

/* ------------------------------------------------------------- members */

function MembersTab() {
  const { t, lang } = useI18n();
  const { can, me, role: myRole, activeOrg } = useSession();

  const membersQuery = useMembers();
  const invitationsQuery = useInvitations();
  const updateRole = useUpdateMemberRole();
  const removeMember = useRemoveMember();
  const revokeInvitation = useRevokeInvitation();

  const [inviting, setInviting] = useState(false);
  const [removing, setRemoving] = useState<Member | null>(null);

  const members = membersQuery.data?.data ?? [];
  const owners = members.filter((member) => member.role === "owner");
  const canManage = can("members:manage");

  // Only an owner may grant `owner` — so an admin is not even offered the option, rather
  // than being handed a select that 403s on the one value that matters.
  const roleOptions = (["owner", "admin", "operator", "viewer"] as OrganizationRole[])
    .filter((r) => r !== "owner" || myRole === "owner")
    .map((r) => ({ value: r, label: t(ROLE_KEY[r]), hint: t(ROLE_HINT[r]) }));

  /**
   * The last owner. The server answers 409 to both demoting and removing them, so the UI
   * does not offer either — but a disabled control with no explanation is just a wall.
   * The rule is stated, with the way out: promote a second owner first.
   */
  const lastOwnerId = owners.length === 1 ? owners[0]!.user_id : null;
  const isLastOwner = (member: Member) => member.user_id === lastOwnerId;

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>{t("settings.members")}</CardTitle>
          {can("invitations:manage") ? (
            <Button size="sm" onClick={() => setInviting(true)}>
              <Plus className="size-4" aria-hidden />
              {t("settings.members.invite")}
            </Button>
          ) : null}
        </CardHeader>

        {membersQuery.isPending ? (
          <LoadingRows rows={3} />
        ) : membersQuery.error ? (
          <ErrorState error={membersQuery.error} onRetry={() => void membersQuery.refetch()} />
        ) : (
          <>
            <div className="w-full overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <caption className="sr-only">{t("settings.members")}</caption>
                <thead>
                  <tr className="border-b border-rule bg-paper-sunk">
                    {[
                      t("auth.name"),
                      t("settings.members.role"),
                      t("settings.members.joined"),
                      t("common.actions"),
                    ].map((heading) => (
                      <th
                        key={heading}
                        scope="col"
                        className="label-caps px-3 py-2 text-start font-mono text-[10px] font-medium text-faint"
                      >
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {members.map((member) => {
                    const isSelf = member.user_id === me?.user.id;
                    const locked = isLastOwner(member);

                    return (
                      <tr key={member.user_id} className="border-b border-rule last:border-b-0">
                        <td className="px-3 py-2.5">
                          <div className="flex flex-col">
                            <span className="font-medium text-text">
                              {member.name}
                              {isSelf ? (
                                <span className="ms-2 text-xs font-normal text-faint">
                                  ({t("settings.members.you")})
                                </span>
                              ) : null}
                            </span>
                            <span className="font-mono text-xs text-faint" dir="ltr">
                              {member.email}
                            </span>
                          </div>
                        </td>

                        <td className="px-3 py-2.5">
                          {canManage && !locked ? (
                            <div className="w-36">
                              <Select
                                value={member.role}
                                aria-label={t("settings.members.role")}
                                disabled={updateRole.isPending}
                                onValueChange={(value) =>
                                  updateRole.mutate({
                                    userId: member.user_id,
                                    role: value as OrganizationRole,
                                  })
                                }
                                options={roleOptions}
                              />
                            </div>
                          ) : (
                            <Badge tone="idle">{t(ROLE_KEY[member.role])}</Badge>
                          )}
                        </td>

                        <td className="px-3 py-2.5 text-xs text-muted">
                          <Num>{formatDate(lang, member.created_at)}</Num>
                        </td>

                        <td className="px-3 py-2.5">
                          {locked ? (
                            <span className="text-xs text-faint">
                              {t("settings.members.lastOwner")}
                            </span>
                          ) : canManage || isSelf ? (
                            // Neutral, deliberately. Removing a person is serious, but it
                            // is not fuel running out, and `dry` is not a mood.
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => setRemoving(member)}
                            >
                              {isSelf ? t("settings.members.leave") : t("settings.members.remove")}
                            </Button>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* The rule, and the way out of it. Stated once, under the table it governs. */}
            {lastOwnerId ? (
              <CardBody className="border-t border-rule pt-3 text-xs leading-relaxed text-muted">
                {t("settings.members.lastOwnerRule")}
              </CardBody>
            ) : null}
          </>
        )}

        {removeMember.error || updateRole.error ? (
          <CardBody className="pt-0">
            <FormError error={removeMember.error ?? updateRole.error} />
          </CardBody>
        ) : null}
      </Card>

      {/* ---- pending invitations ---- */}
      {can("invitations:manage") ? (
        <Card>
          <CardHeader>
            <CardTitle>{t("settings.invite.pending")}</CardTitle>
          </CardHeader>

          {invitationsQuery.isPending ? (
            <LoadingRows rows={2} />
          ) : invitationsQuery.error ? (
            <ErrorState
              error={invitationsQuery.error}
              onRetry={() => void invitationsQuery.refetch()}
            />
          ) : (invitationsQuery.data?.data.length ?? 0) === 0 ? (
            <EmptyState title={t("settings.invite.none")} />
          ) : (
            <CardBody className="flex flex-col gap-2">
              {invitationsQuery.data?.data.map((invitation) => (
                <div
                  key={invitation.id}
                  className="flex flex-wrap items-center justify-between gap-3 border-b border-rule pb-2 last:border-b-0 last:pb-0"
                >
                  <div className="min-w-0">
                    <p className="truncate font-mono text-sm text-text" dir="ltr">
                      {invitation.email}
                    </p>
                    <p className="text-xs text-faint">
                      {t(ROLE_KEY[invitation.role])} · {t("settings.invite.expires")}{" "}
                      <Num>{formatDate(lang, invitation.expires_at)}</Num>
                    </p>
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={revokeInvitation.isPending}
                    onClick={() => revokeInvitation.mutate(invitation.id)}
                  >
                    {t("settings.invite.revoke")}
                  </Button>
                </div>
              ))}
            </CardBody>
          )}

          {revokeInvitation.error ? (
            <CardBody className="pt-0">
              <FormError error={revokeInvitation.error} />
            </CardBody>
          ) : null}
        </Card>
      ) : null}

      <InviteDialog open={inviting} onOpenChange={setInviting} roleOptions={roleOptions} />

      <RemoveMemberDialog
        member={removing}
        isSelf={removing?.user_id === me?.user.id}
        orgName={activeOrg?.name ?? ""}
        pending={removeMember.isPending}
        onCancel={() => setRemoving(null)}
        onConfirm={() => {
          if (!removing) return;
          removeMember.mutate(removing.user_id, { onSuccess: () => setRemoving(null) });
        }}
      />
    </div>
  );
}

/**
 * Removing a person is not an emergency, so it does not get the emergency colour. It gets
 * a dialog — a deliberate second beat — and the plain sentence about what it does.
 */
function RemoveMemberDialog({
  member,
  isSelf,
  orgName,
  pending,
  onCancel,
  onConfirm,
}: {
  member: Member | null;
  isSelf: boolean;
  orgName: string;
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { t } = useI18n();
  if (!member) return null;

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
      title={isSelf ? t("settings.members.leaveTitle") : t("settings.members.removeTitle")}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel}>
            {t("common.cancel")}
          </Button>
          <Button variant="secondary" disabled={pending} onClick={onConfirm}>
            {pending
              ? t("common.saving")
              : isSelf
                ? t("settings.members.leave")
                : t("settings.members.remove")}
          </Button>
        </>
      }
    >
      <p className="text-sm text-text">
        {isSelf
          ? t("settings.members.leaveConfirm", { org: orgName })
          : t("settings.members.removeConfirm", { name: member.name })}
      </p>
    </Dialog>
  );
}

function InviteDialog({
  open,
  onOpenChange,
  roleOptions,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roleOptions: Array<{ value: string; label: string; hint?: string }>;
}) {
  const { t } = useI18n();
  const invite = useCreateInvitation();

  const [email, setEmail] = useState("");
  const [role, setRole] = useState<OrganizationRole>("operator");

  const fieldErrors = invite.error instanceof ApiError ? invite.error.fieldErrors : {};

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("settings.members.invite")}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            disabled={invite.isPending || !email.trim()}
            onClick={() =>
              invite.mutate(
                { email: email.trim(), role },
                {
                  onSuccess: () => {
                    onOpenChange(false);
                    setEmail("");
                  },
                },
              )
            }
          >
            {invite.isPending ? t("common.saving") : t("settings.invite.send")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <FormError
          error={invite.error instanceof ApiError && invite.error.isFormLevel ? invite.error : null}
        />

        <Field label={t("settings.invite.email")} error={fieldErrors["email"]} required>
          {(props) => (
            <Input
              {...props}
              type="email"
              dir="ltr"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          )}
        </Field>

        <Field label={t("settings.invite.role")} error={fieldErrors["role"]} required>
          {(props) => (
            <Select
              {...props}
              value={role}
              onValueChange={(value) => setRole(value as OrganizationRole)}
              options={roleOptions}
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
}

/* ------------------------------------------------------------ api keys */

const SCOPES: ReadonlyArray<{ value: ApiKeyScope; label: TKey; hint: TKey }> = [
  { value: "ingest:write", label: "settings.keys.scope.ingest", hint: "settings.keys.scope.ingestHint" },
  { value: "data:read", label: "settings.keys.scope.read", hint: "settings.keys.scope.readHint" },
];

const SCOPE_LABEL: Record<ApiKeyScope, TKey> = {
  "ingest:write": "settings.keys.scope.ingest",
  "data:read": "settings.keys.scope.read",
};

function ApiKeysTab({ isOwner }: { isOwner: boolean }) {
  const { t, lang } = useI18n();
  const keysQuery = useApiKeys();
  const revoke = useRevokeApiKey();

  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<ApiKeyCreated | null>(null);
  const [revoking, setRevoking] = useState<ApiKey | null>(null);

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{t("settings.apiKeys")}</CardTitle>
          {isOwner ? (
            <Button size="sm" onClick={() => setCreating(true)}>
              <Plus className="size-4" aria-hidden />
              {t("settings.keys.new")}
            </Button>
          ) : (
            // An admin can see the keys but cannot mint one. Say so, rather than rendering
            // a button that is guaranteed to come back 403.
            <span className="text-xs text-faint">{t("settings.ownerOnly")}</span>
          )}
        </CardHeader>

        {keysQuery.isPending ? (
          <LoadingRows rows={2} />
        ) : keysQuery.error ? (
          <ErrorState error={keysQuery.error} onRetry={() => void keysQuery.refetch()} />
        ) : (keysQuery.data?.data.length ?? 0) === 0 ? (
          <EmptyState
            icon={<KeyRound className="size-6" />}
            title={t("settings.keys.empty")}
            body={t("settings.keys.emptyBody")}
            action={
              isOwner ? (
                <Button size="sm" onClick={() => setCreating(true)}>
                  {t("settings.keys.new")}
                </Button>
              ) : null
            }
          />
        ) : (
          <CardBody className="flex flex-col gap-3">
            {keysQuery.data?.data.map((key) => (
              <div
                key={key.id}
                className="flex flex-wrap items-center justify-between gap-3 border-b border-rule pb-3 last:border-b-0 last:pb-0"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-text">{key.name}</p>
                  {/* All that is left of the key: its prefix. That is the whole point. */}
                  <p className="font-mono text-xs text-faint" dir="ltr">
                    {key.prefix}…
                  </p>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {key.scopes.map((scope) => (
                      <Badge key={scope} tone="idle">
                        {t(SCOPE_LABEL[scope])}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="flex shrink-0 items-center gap-4">
                  <div className="text-end">
                    <MicroLabel>{t("settings.keys.lastUsed")}</MicroLabel>
                    <p className="text-xs text-muted">
                      <Num>
                        {key.last_used_at
                          ? formatDate(lang, key.last_used_at)
                          : t("settings.keys.never")}
                      </Num>
                    </p>
                    {/* "Expires — No expiry" is not a sentence. A key with no expiry says
                        one thing, and it is not a date. */}
                    <p className="text-xs text-faint">
                      {key.expires_at ? (
                        <>
                          {t("settings.keys.expires")}{" "}
                          <Num>{formatDate(lang, key.expires_at)}</Num>
                        </>
                      ) : (
                        t("settings.keys.noExpiry")
                      )}
                    </p>
                  </div>

                  {isOwner ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={revoke.isPending}
                      onClick={() => setRevoking(key)}
                    >
                      {t("settings.keys.revoke")}
                    </Button>
                  ) : null}
                </div>
              </div>
            ))}
          </CardBody>
        )}

        {revoke.error ? (
          <CardBody className="pt-0">
            <FormError error={revoke.error} />
          </CardBody>
        ) : null}
      </Card>

      <CreateKeyDialog
        open={creating}
        onOpenChange={setCreating}
        onCreated={(result) => {
          setCreating(false);
          setCreated(result);
        }}
      />

      <ShowKeyOnceDialog created={created} onClose={() => setCreated(null)} />

      {revoking ? (
        <Dialog
          open
          onOpenChange={(open) => {
            if (!open) setRevoking(null);
          }}
          title={t("settings.keys.revokeTitle")}
          footer={
            <>
              <Button variant="ghost" onClick={() => setRevoking(null)}>
                {t("common.cancel")}
              </Button>
              <Button
                variant="secondary"
                disabled={revoke.isPending}
                onClick={() =>
                  revoke.mutate(revoking.id, { onSuccess: () => setRevoking(null) })
                }
              >
                {revoke.isPending ? t("common.saving") : t("settings.keys.revoke")}
              </Button>
            </>
          }
        >
          <p className="text-sm text-text">
            {t("settings.keys.revokeConfirm", { name: revoking.name })}
          </p>
        </Dialog>
      ) : null}
    </>
  );
}

function CreateKeyDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (result: ApiKeyCreated) => void;
}) {
  const { t } = useI18n();
  const createKey = useCreateApiKey();

  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<ApiKeyScope[]>(["ingest:write"]);
  const [expiry, setExpiry] = useState("");

  const fieldErrors = createKey.error instanceof ApiError ? createKey.error.fieldErrors : {};

  const toggleScope = (scope: ApiKeyScope) => {
    setScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope],
    );
  };

  const submit = () => {
    createKey.mutate(
      {
        name: name.trim(),
        scopes,
        // A date input gives a naive day; the contract wants an instant. The key expires
        // at the END of the day the owner picked, not at midnight as it begins.
        ...(expiry ? { expires_at: new Date(`${expiry}T23:59:59`).toISOString() } : {}),
      },
      {
        onSuccess: (result) => {
          setName("");
          setScopes(["ingest:write"]);
          setExpiry("");
          onCreated(result);
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("settings.keys.new")}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            disabled={createKey.isPending || !name.trim() || scopes.length === 0}
            onClick={submit}
          >
            {createKey.isPending ? t("common.saving") : t("common.create")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <FormError
          error={
            createKey.error instanceof ApiError && createKey.error.isFormLevel
              ? createKey.error
              : null
          }
        />

        <Field label={t("settings.keys.name")} error={fieldErrors["name"]} required>
          {(props) => (
            <Input
              {...props}
              value={name}
              placeholder="Karrada tank sensors"
              onChange={(event) => setName(event.target.value)}
            />
          )}
        </Field>

        {/* Scopes are a SET, not a choice — a sensor key that both pushes readings and
            reads back the tank it is pushing to is an ordinary thing to want. */}
        <fieldset className="flex flex-col gap-1.5">
          <legend className="text-sm font-medium text-text">
            {t("settings.keys.scopes")}
            <span aria-hidden className="ms-1 text-dry">
              *
            </span>
          </legend>
          <p className="text-xs text-faint">{t("settings.keys.scopesHint")}</p>

          <div className="mt-1.5 flex flex-col gap-2">
            {SCOPES.map((scope) => (
              // A scope the customer believes they withheld, and did not, is a permission
              // leak. So "checked" is said twice — by the box AND by the whole row lighting
              // up — and never by a tint you could squint past.
              <label
                key={scope.value}
                className={cn(
                  "flex cursor-pointer items-start gap-2.5 rounded-[8px] border px-3 py-2.5",
                  "transition-colors hover:border-mauve",
                  "has-[:checked]:border-cream has-[:checked]:bg-raised-hi",
                  "has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-cream",
                  "border-rule-strong bg-paper-sunk",
                )}
              >
                <input
                  type="checkbox"
                  className="mt-0.5 size-4 shrink-0 accent-cream"
                  checked={scopes.includes(scope.value)}
                  onChange={() => toggleScope(scope.value)}
                />
                <span className="flex flex-col">
                  <span className="text-sm font-medium text-text">{t(scope.label)}</span>
                  <span className="text-xs text-faint">{t(scope.hint)}</span>
                </span>
              </label>
            ))}
          </div>

          {scopes.length === 0 ? (
            <p role="alert" className="text-xs font-medium text-dry">
              {t("settings.keys.scopesRequired")}
            </p>
          ) : null}
          {fieldErrors["scopes"] ? (
            <p role="alert" className="text-xs font-medium text-dry">
              {fieldErrors["scopes"]}
            </p>
          ) : null}
        </fieldset>

        <Field
          label={t("settings.keys.expiry")}
          hint={t("settings.keys.expiryHint")}
          error={fieldErrors["expires_at"]}
        >
          {(props) => (
            <Input
              {...props}
              type="date"
              dir="ltr"
              className="font-mono"
              value={expiry}
              onChange={(event) => setExpiry(event.target.value)}
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
}

/**
 * The key, shown ONCE.
 *
 * The server keeps an argon2id hash and nothing else — it genuinely cannot show this
 * again, and saying so bluntly is the honest thing rather than the scary thing.
 *
 * The warning is a slab of CREAM, not `dry`: on a plum page cream is the loudest surface
 * there is (it is why the primary button is made of it), and `dry` is reserved for fuel
 * running out. A warning that borrows the alarm colour is how the alarm colour stops
 * meaning anything.
 *
 * It is built from FORM, not colour.
 *
 * `dry` is not available (it means fuel running out, and nothing else), but a solid cream
 * slab is not the answer either: a full cream rectangle is the primary Button, so a warning
 * shaped like one reads as clickable — and the one thing that must never happen here is a
 * customer clicking the *warning* instead of Copy and closing the only chance they will get
 * to see this key. So:
 *
 *   - the warning is cream PROSE behind a cream hairline. Loud, and unmistakably text.
 *   - the key sits in a sunk mono well. It is a credential; it should look like one.
 *   - the only slab of cream in this dialog is COPY KEY — the loudest thing on the page is
 *     the thing we want them to do. Same logic as the sign-in button, applied honestly.
 *
 * There is no ✕, no Escape and no backdrop dismiss: the cost of a mis-click here is a dead
 * sensor gateway and no way back but minting a new key. You have to say you saved it.
 * (Suppressing close would otherwise leave the Dialog's ✕ rendered and inert — a control
 * that does nothing is a lie — so the ✕ is hidden along with the behaviour it implies.)
 */
function ShowKeyOnceDialog({
  created,
  onClose,
}: {
  created: ApiKeyCreated | null;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (created) setCopied(false);
  }, [created]);

  if (!created) return null;

  return (
    <Dialog
      open
      // Nothing closes this but the acknowledgement below — not Escape, not the backdrop.
      onOpenChange={() => undefined}
      title={t("settings.keys.created")}
      // ...and so the ✕ the primitive draws in its header is hidden, rather than left
      // sitting there looking like a way out that silently does nothing.
      className="[&>div:first-child>button]:hidden"
    >
      <div className="flex flex-col gap-4">
        {/* The warning: cream prose behind a cream hairline. It cannot be mistaken for a
            button, and it cannot be missed. */}
        <div className="flex items-start gap-2.5 border-s-2 border-cream ps-3">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-cream" aria-hidden />
          <div>
            <p className="text-sm leading-relaxed font-semibold text-cream">
              {t("settings.keys.showOnce")}
            </p>
            <p className="mt-1.5 text-xs leading-relaxed text-muted">
              {t("settings.keys.showOnceBody")}
            </p>
          </div>
        </div>

        {/* The credential, in a well. Selectable in one gesture. */}
        <code
          dir="ltr"
          className="num block w-full select-all overflow-x-auto rounded-[8px] border border-rule-strong bg-paper-sunk px-4 py-3.5 text-[13px] text-text"
        >
          {created.key}
        </code>

        {/* The only cream in the dialog is the thing we want them to do. */}
        <Button
          className="w-full"
          onClick={() => {
            void navigator.clipboard?.writeText(created.key).then(() => setCopied(true));
          }}
        >
          {copied ? (
            <>
              <Check className="size-4" aria-hidden />
              {t("common.copied")}
            </>
          ) : (
            <>
              <Copy className="size-4" aria-hidden />
              {t("settings.keys.copy")}
            </>
          )}
        </Button>

        <Button variant="secondary" onClick={onClose} className="w-full">
          {t("settings.keys.ack")}
        </Button>
      </div>
    </Dialog>
  );
}
